import { Link } from 'react-router-dom';
import { useSEO } from '@/hooks/useSEO';

const values = [
  {
    icon: 'ri-focus-3-line',
    title: '题材聚焦',
    desc: '只做「双女主」这一件事，收录的每一部作品都有清晰的题材标签，找漫更精准。',
  },
  {
    icon: 'ri-speed-up-line',
    title: '阅读流畅',
    desc: '宽松的行距、清爽的排版与响应式布局，让长时间追漫也不易疲惫。',
  },
  {
    icon: 'ri-shield-check-line',
    title: '完全免费',
    desc: '所有收录作品均可在站内免费在线阅读，无需登录、无需付费。',
  },
  {
    icon: 'ri-heart-3-line',
    title: '轻松收藏',
    desc: '一键收藏喜欢的作品，收藏状态保存在本地，下次打开即可找回。',
  },
];

export default function About() {
  useSEO({
    title: '关于我们 - 双女主韩漫免费在线阅读平台',
    description:
      '了解双女主韩漫免费在线阅读网站的定位、内容特色与使用说明。我们专注收录最新热门双女主韩漫，致力于提供舒适的在线阅读体验。',
    keywords: '关于我们,双女主韩漫,韩漫免费观看,在线阅读平台',
  });

  return (
    <div className="mx-auto w-full px-4 py-8 md:px-6 md:py-10">
      <nav className="mb-6 flex flex-wrap items-center gap-1 text-xs text-foreground-500">
        <Link to="/" className="transition-colors hover:text-primary-600">
          首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-800">关于我们</span>
      </nav>

      <section className="relative overflow-hidden rounded-lg border border-background-200">
        <div className="relative h-56 w-full md:h-72">
          <img
            src="https://readdy.ai/api/search-image?query=Stylized%20artistic%20illustration%20of%20a%20bright%20modern%20reading%20studio%20with%20comic%20shelves%20and%20two%20young%20women%20browsing%20books%2C%20soft%20warm%20coral%20and%20teal%20tones%2C%20clean%20airy%20background%2C%20gentle%20natural%20light%2C%20editorial%20lifestyle%20illustration%2C%20harmonious%20composition%2C%20high%20detail&width=1600&height=800&seq=about-hero-01&orientation=landscape"
            alt="双女主韩漫在线阅读平台"
            title="关于双女主韩漫在线阅读平台"
            className="h-full w-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/85 via-foreground-950/60 to-foreground-950/30"></div>
          <div className="absolute inset-0 flex flex-col justify-center px-6 md:px-12">
            <span className="mb-3 flex w-fit items-center gap-1 rounded-full bg-primary-500 px-3 py-1 text-xs font-semibold text-background-50">
              <i className="ri-emotion-happy-line"></i>
              关于我们
            </span>
            <h1 className="text-2xl font-bold text-background-50 md:text-4xl">
              我们专注双女主韩漫的在线阅读
            </h1>
            <p className="mt-3 max-w-2xl text-xs leading-relaxed text-background-200 md:text-sm">
              从校园到奇幻，从恋爱到悬疑，我们希望用最清晰的分类与最舒适的方式，陪你读完每一段属于她的故事。
            </p>
          </div>
        </div>
      </section>

      <section className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-[1.5fr_1fr]">
        <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
          <h2 className="flex items-center gap-2 text-xl font-bold text-foreground-950">
            <i className="ri-quill-pen-line text-primary-500"></i>
            网站定位与内容介绍
          </h2>
          <div className="mt-4 flex flex-col gap-4 text-sm leading-relaxed text-foreground-600">
            <p>
              「韩漫免费观看在线阅读漫画双女主」是一个专门面向喜爱韩漫、尤其是偏爱
              <strong className="text-foreground-900">双女主题材</strong>
              的年轻漫画爱好者的在线阅读平台。
            </p>
            <p>
              我们围绕双女主这一核心主题，持续收录最新、最热门的韩漫作品，并为每部作品提供封面、剧情简介、作者信息、
              连载状态与完整的章节目录，方便你快速了解内容后再决定是否追读。
            </p>
            <p>
              站内作品按校园、恋爱、奇幻、都市、治愈、悬疑、古风等题材分类，配合关键词搜索与多维筛选，
              让你能从海量内容中迅速找到心仪的作品。
            </p>
          </div>

          <h2 className="mt-8 flex items-center gap-2 text-xl font-bold text-foreground-950">
            <i className="ri-question-line text-accent-600"></i>
            使用说明
          </h2>
          <ol className="mt-4 flex flex-col gap-3 text-sm leading-relaxed text-foreground-600">
            {[
              '在顶部搜索框输入作品名、作者或题材关键词，回车即可进入搜索结果页。',
              '通过「分类浏览」进入任一题材分类，使用左侧筛选栏按连载状态、题材与年份筛选，并切换排序方式。',
              '在漫画详情页可查看章节目录、剧情简介与相关推荐，点击章节名可浏览章节列表。',
              '点击「收藏本作」把喜欢的漫画加入收藏夹，收藏状态保存在本地浏览器中，随时可以取消。',
            ].map((step, index) => (
              <li key={step} className="flex gap-3">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-100 text-xs font-bold text-primary-700">
                  {index + 1}
                </span>
                <span>{step}</span>
              </li>
            ))}
          </ol>
        </div>

        <div className="flex flex-col gap-6">
          <div className="rounded-lg border border-background-200 bg-background-50 p-6">
            <h2 className="flex items-center gap-2 text-lg font-bold text-foreground-950">
              <i className="ri-customer-service-2-line text-primary-500"></i>
              联系我们
            </h2>
            <ul className="mt-4 flex flex-col gap-3 text-sm text-foreground-600">
              <li className="flex items-center gap-2">
                <span className="flex h-8 w-8 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                  <i className="ri-mail-line"></i>
                </span>
                hello@shuangnuzhu-comic.example
              </li>
              <li className="flex items-center gap-2">
                <span className="flex h-8 w-8 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                  <i className="ri-wechat-line"></i>
                </span>
                微信公众号：shuangnuzhu_comic
              </li>
              <li className="flex items-center gap-2">
                <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                  <i className="ri-weibo-line"></i>
                </span>
                微博：@双女主韩漫阅读
              </li>
              <li className="flex items-center gap-2">
                <span className="flex h-8 w-8 items-center justify-center rounded-md bg-background-100 text-foreground-600">
                  <i className="ri-time-line"></i>
                </span>
                回复时间：每周一至周五 10:00 - 18:00
              </li>
            </ul>
            <p className="mt-4 rounded-md bg-background-100 px-4 py-3 text-xs leading-relaxed text-foreground-500">
              如对内容收录、版权或合作有任何疑问，欢迎通过上方任一方式与我们联系，我们会尽快回复。
            </p>
          </div>

          <div className="rounded-lg border border-background-200 bg-secondary-50 p-6">
            <h2 className="flex items-center gap-2 text-lg font-bold text-secondary-950">
              <i className="ri-information-line"></i>
              内容声明
            </h2>
            <p className="mt-3 text-xs leading-relaxed text-secondary-900">
              本站当前为演示站点，页面内展示的漫画作品、作者、章节与数据均为虚拟示范内容，用于展示产品功能与界面设计，
              不涉及任何真实版权作品。搜索、筛选与收藏均为前端演示效果，收藏数据保存在本地浏览器中。
            </p>
          </div>
        </div>
      </section>

      <section className="mt-8">
        <h2 className="mb-5 text-xl font-bold text-foreground-950">我们想坚持的事</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {values.map((value) => (
            <div
              key={value.title}
              className="rounded-lg border border-background-200 bg-background-50 p-5 transition-colors hover:border-primary-300"
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                <i className={`${value.icon} text-xl`}></i>
              </span>
              <h3 className="mt-4 text-base font-bold text-foreground-950">{value.title}</h3>
              <p className="mt-2 text-xs leading-relaxed text-foreground-500">{value.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-10 flex flex-col items-center justify-between gap-4 rounded-lg bg-accent-900 px-6 py-8 text-center sm:flex-row sm:text-left">
        <div>
          <h2 className="text-xl font-bold text-background-50">准备好开始追漫了吗？</h2>
          <p className="mt-1 text-sm text-background-200">从热门榜单或题材分类出发，找到你的下一部双女主韩漫。</p>
        </div>
        <div className="flex flex-wrap justify-center gap-3">
          <Link
            to="/"
            className="flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-fire-line"></i>
            浏览热门
          </Link>
          <Link
            to="/search"
            className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-50/40 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-background-50/10"
          >
            <i className="ri-search-line"></i>
            搜索漫画
          </Link>
        </div>
      </section>
    </div>
  );
}